import React, { useState, useEffect, useCallback } from "react";
import { Menu, Sidebar, Icon } from "semantic-ui-react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showAttendanceSubmittedRequestsPage } from "../../domain/attendance/attendanceAccessUtils";
import { canAccessMyAppraisal, getAppraisalHomePath } from "../../domain/appraisal/appraisalAccess";
import {
  isAppraisalFeatureEnabled,
  useAppraisalFeatures,
} from "../../domain/appraisal/appraisalFeatures";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";
import {
  getUserFromStorage,
  navigateToMyCalendar,
  navigateToWallChart,
} from "../../../utils/workplaceNavigation";
import {
  exitSuperAdminCompany,
  isSuperAdminInCompany,
  isSuperAdminPlatformMode,
  isSuperAdminRole,
} from "../../../utils/superAdminContext";
import { useSubscriptionStatus } from "../../../hooks/useSubscriptionStatus";
import { getSelectedCompany } from "../../Global/CustomFunctions";

import "./SidebarMenu.css";

/** Keep expanded/collapsed widths in sync with `getSidebarWidth` in Layout.jsx */
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const EXPANDED_GROUPS_STORAGE_KEY = "sidebarNavGroupsExpandedV2";
const SUBSCRIPTION_RESTRICTED_MENU_KEYS = new Set([
  "documents",
  "timesheets",
  "attendance",
  "payroll",
  "appraisal",
]);

const defaultExpandedGroups = () => ({
  leaveSettings: false,
  organization: false,
  documents: false,
  timesheets: false,
  attendance: false,
  payroll: false,
});

const readExpandedGroups = () => {
  try {
    const raw = localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...defaultExpandedGroups(), ...parsed };
    }
  } catch (_) {
    /* ignore */
  }
  return defaultExpandedGroups();
};

const SidebarMenu = ({ collapsed, onToggle, isMobile = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const [expandedGroups, setExpandedGroups] = useState(readExpandedGroups);
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("default");
  const { features: appraisalFeatures, loading: appraisalFeaturesLoading } = useAppraisalFeatures();
  const { isSuspended, loading: subscriptionLoading } = useSubscriptionStatus();

  const sidebarWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;

  const getUserRoleId = () => {
    try {
      const { WebStorage, WebStorageNames } = require("../../../utils");
      return WebStorage.getLocalStore(WebStorageNames.RoleId);
    } catch (e) {
      return localStorage.getItem("roleId");
    }
  };

  const userRoleId = getUserRoleId();

  const handleRedirect = (path) => {
    history.push(path);
  };

  const persistExpandedGroups = useCallback((next) => {
    try {
      localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      /* ignore */
    }
  }, []);

  const toggleGroup = useCallback(
    (key) => {
      setExpandedGroups((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        persistExpandedGroups(next);
        return next;
      });
    },
    [persistExpandedGroups]
  );

  const isLeaveSettingsPath = (pathname = location.pathname) => {
    const p = pathname;
    return (
      p.startsWith("/settings/leave") ||
      p === "/settings/carryforward" ||
      p === "/settings/carry-forward" ||
      p === "/settings/leavetypes" ||
      p === "/settings/leave-types" ||
      p === "/settings/publicHolidays" ||
      p === "/settings/public-holidays" ||
      p === "/settings/security"
    );
  };

  useEffect(() => {
    const p = location.pathname;
    setExpandedGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      if (p.startsWith("/documents/") && !next.documents) {
        next.documents = true;
        changed = true;
      }
      if (p.startsWith("/timesheets") && !next.timesheets) {
        next.timesheets = true;
        changed = true;
      }
      if (p.startsWith("/attendance") && !next.attendance) {
        next.attendance = true;
        changed = true;
      }
      if (p.startsWith("/payroll") && !next.payroll) {
        next.payroll = true;
        changed = true;
      }
      if (
        (p.startsWith("/settings/organization") || p === "/settings/departments") &&
        !next.organization
      ) {
        next.organization = true;
        changed = true;
      }
      if (
        (isLeaveSettingsPath(p) ||
          p === "/leave/dashboard" ||
          p.includes("/timeOff") ||
          p.includes("/time-off") ||
          p.includes("/dashboard/calender")) &&
        !next.leaveSettings
      ) {
        next.leaveSettings = true;
        changed = true;
      }
      if (p.startsWith("/appraisal") && !next.appraisal) {
        next.appraisal = true;
        changed = true;
      }
      if (changed) {
        persistExpandedGroups(next);
      }
      return changed ? next : prev;
    });
  }, [location.pathname, persistExpandedGroups]);

  const isWorkplacesPath = () => {
    const p = location.pathname;
    return (
      p === "/workplaces" ||
      p === "/companies" ||
      /\/offices(\/|$)/.test(p)
    );
  };

  const isCompanySettingsPath = () => {
    const p = location.pathname;
    return (
      p.startsWith("/settings/company") ||
      p === "/settings/general" ||
      p === "/settings/billing" ||
      p === "/settings/dangerzone" ||
      p === "/settings/danger-zone"
    );
  };

  const isActive = (path) => {
    if (path === "/users") {
      return (
        location.pathname.includes("/users") ||
        location.pathname.includes("/user-form") ||
        location.pathname === "/profile"
      );
    }
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    if (path === "/workplaces") {
      return isWorkplacesPath();
    }
    if (path === "/settings/company") {
      if (!isCompanySettingsPath()) return false;
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab") || "general";
      return tab !== "notifications";
    }
    if (path === "/settings/leave") {
      return isLeaveSettingsPath();
    }
    if (path === "__wallchart__") {
      return location.pathname === "/wallchart" || location.pathname.includes("/time-off") || location.pathname.includes("/timeOff");
    }
    if (path === "__mycalendar__") {
      return location.pathname.includes("/dashboard/calender");
    }
    if (path === "/timesheets/time-log") {
      return (
        location.pathname === "/timesheets/time-log" ||
        location.pathname.startsWith("/timesheets/time-log/") ||
        location.pathname === "/timesheets/timelog" ||
        location.pathname.startsWith("/timesheets/timelog/")
      );
    }
    if (path === "/timesheets/reports") {
      return location.pathname === "/timesheets/reports";
    }
    if (path === "/timesheets/approvals") {
      return location.pathname === "/timesheets/approvals";
    }
    if (path === "/attendance/hub") {
      return location.pathname === "/attendance" || location.pathname === "/attendance/hub";
    }
    if (path === "/attendance/daily-logs") {
      return location.pathname === "/attendance/daily-logs" || location.pathname === "/attendance/dailyLogs";
    }
    if (path === "/attendance/corrections") {
      return location.pathname === "/attendance/corrections";
    }
    if (path === "/attendance/submitted-requests") {
      return location.pathname === "/attendance/submitted-requests";
    }
    if (path === "/attendance/reports") {
      return location.pathname === "/attendance/reports";
    }
    if (path === "/attendance/settings") {
      return location.pathname === "/attendance/settings";
    }
    if (path === "/payroll/overview") {
      return location.pathname === "/payroll" || location.pathname === "/payroll/overview";
    }
    if (path.startsWith("/payroll/")) {
      return location.pathname === path;
    }
    // Exact match, or nested route under this path (e.g. /appraisal/pdp/123).
    // Do not use includes() — "/appraisal/team" must not match "/appraisal/team-one-to-one".
    const pathname = location.pathname;
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const menuItems = [
    {
      path: "/dashboard",
      icon: "th large",
      label: t("app.sidebar.dashboard"),
      description: (parseInt(userRoleId) === 3 || parseInt(userRoleId) === 4) ? t("app.sidebar.dashboardOverview") : t("app.sidebar.dashboardOverviewAnalytics"),
    },
    {
      path: "/workplaces",
      icon: "building",
      label: t("app.sidebar.workplaces"),
      description: t("app.sidebar.workplacesDescription"),
    },
    {
      path: "/users",
      icon: "users",
      label: t("app.sidebar.employee"),
      description: t("app.sidebar.manageEmployees"),
      roles: [1, 2],
    },
    {
      key: "leaveSettings",
      icon: "calendar alternate outline",
      label: t("app.sidebar.leave"),
      isSectionActive: (p) =>
        isLeaveSettingsPath(p) ||
        p === "/leave/dashboard" ||
        p === "/wallchart" ||
        p.includes("/time-off") ||
        p.includes("/timeOff") ||
        p.includes("/dashboard/calender"),
      onCollapsedClick: (_rid, navigate) => navigate("/settings/leave"),
      roles: [1, 2],
      children: [
        {
          path: "/leave/dashboard",
          icon: "chart bar",
          label: t("app.sidebar.leaveDashboard"),
          roles: [1, 2],
        },
        {
          navId: "wallchart",
          icon: "th",
          label: t("app.sidebar.wallChart"),
          requiresWorkplace: true,
          roles: [1, 2],
        },
        {
          navId: "mycalendar",
          icon: "calendar outline",
          label: t("app.sidebar.myCalendar"),
          requiresWorkplace: true,
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "leavetypes",
          icon: "calendar alternate outline",
          label: t("app.settingsPage.leaveTypes"),
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "carryforward",
          icon: "refresh",
          label: t("app.settingsPage.carryForward"),
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "publicHolidays",
          icon: "flag",
          label: t("app.settingsPage.publicHolidays"),
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "backup",
          icon: "cloud download",
          label: t("app.settingsPage.reporting"),
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "privacy",
          icon: "lock",
          label: t("app.settingsPage.privacyVisibility"),
          roles: [1, 2],
        },
        {
          path: "/settings/leave",
          tab: "autoDeduction",
          icon: "calendar minus outline",
          label: t("app.settingsPage.autoLeaveDeduction"),
          roles: [1, 2],
        },
      ],
    },
    {
      key: "organization",
      icon: "sitemap",
      label: t("app.sidebar.organization"),
      isSectionActive: (p) => p.startsWith("/settings/organization") || p === "/settings/departments",
      onCollapsedClick: (rid, navigate) => {
        if (rid === 1 || rid === 2) navigate("/settings/organization/departments");
      },
      children: [
        { path: "/settings/organization/departments", icon: "building", label: t("app.sidebar.organizationDepartments"), roles: [1, 2] },
        { path: "/settings/organization/designations", icon: "certificate", label: t("app.sidebar.organizationDesignations"), roles: [1, 2] },
        { path: "/settings/organization/employee-types", icon: "address card outline", label: t("app.sidebar.organizationEmploymentTypes"), roles: [1, 2] },
        { path: "/settings/organization/teams", icon: "users", label: t("app.sidebar.organizationTeams"), roles: [1, 2] },
        { path: "/settings/organization/projects", icon: "briefcase", label: t("app.sidebar.organizationProjects"), roles: [1, 2] },
      ],
    },
    {
      key: "documents",
      icon: "folder open",
      label: t("app.sidebar.documents"),
      isSectionActive: (p) => p.startsWith("/documents/"),
      onCollapsedClick: (rid, navigate) => {
        if (rid === 1 || rid === 2) navigate("/documents/library");
        else if (rid === 3 || rid === 4) navigate("/documents/document-portal");
        else navigate("/workplaces");
      },
      children: [
        { path: "/documents/library", icon: "folder", label: t("app.sidebar.documentsLibrary"), roles: [1, 2] },
        { path: "/documents/compliance", icon: "check circle", label: t("app.sidebar.documentsCompliance"), roles: [1, 2] },
        { path: "/documents/document-portal", icon: "check square outline", label: t("app.sidebar.documentsPortal"), roles: [3, 4] },
      ],
    },
    {
      key: "timesheets",
      icon: "clock outline",
      label: t("app.sidebar.timesheets"),
      isSectionActive: (p) => p.startsWith("/timesheets"),
      onCollapsedClick: (_rid, navigate) => navigate("/timesheets/time-log"),
      children: [
        { path: "/timesheets/time-log", icon: "calendar outline", label: t("app.sidebar.timesheetsTimeLog"), roles: [1, 2, 3, 4] },
        { path: "/timesheets/approvals", icon: "check circle", label: t("app.sidebar.timesheetsApprovals"), roles: [1, 2, 3] },
        { path: "/timesheets/reports", icon: "bar chart", label: t("app.sidebar.timesheetsReports"), roles: [1, 2] },
      ],
    },
    {
      key: "attendance",
      icon: "calendar outline",
      label: t("app.sidebar.attendance"),
      isSectionActive: (p) => p.startsWith("/attendance"),
      onCollapsedClick: (_rid, navigate) => navigate("/attendance/hub"),
      children: [
        { path: "/attendance/hub", icon: "clock outline", label: t("app.sidebar.attendanceHub"), roles: [1, 2, 3, 4] },
        { path: "/attendance/daily-logs", icon: "list ul", label: t("app.sidebar.attendanceDailyLogs"), roles: [1, 2, 3, 4] },
        {
          path: "/attendance/corrections",
          icon: "edit",
          label: t("app.sidebar.attendanceCorrectionsQueue"),
          roles: [1, 2, 3],
        },
        {
          path: "/attendance/submitted-requests",
          icon: "send",
          label: t("app.sidebar.attendanceSubmittedRequests"),
          roles: [1, 2, 3, 4],
          visibleWhen: (rid) => showAttendanceSubmittedRequestsPage(rid),
        },
        { path: "/attendance/reports", icon: "bar chart", label: t("app.sidebar.attendanceReports"), roles: [1, 2] },
        { path: "/attendance/settings", icon: "setting", label: t("app.sidebar.attendanceConfiguration"), roles: [1, 2] },
        { path: "/attendance/machines", icon: "desktop", label: t("app.sidebar.attendanceMachines"), roles: [1, 2] },
      ],
    },
    {
      key: "payroll",
      icon: "money bill alternate outline",
      label: t("app.sidebar.payroll"),
      isSectionActive: (p) => p.startsWith("/payroll"),
      onCollapsedClick: (_rid, navigate) => navigate("/payroll/overview"),
      children: [
        { path: "/payroll/overview", icon: "chart area", label: t("app.sidebar.payrollOverview"), roles: [1, 2] },
        { path: "/payroll/salary-components", icon: "th list", label: t("app.sidebar.payrollSalaryComponents"), roles: [1, 2] },
        { path: "/payroll/payroll-structures", icon: "sitemap", label: t("app.sidebar.payrollStructures"), roles: [1, 2] },
        { path: "/payroll/income-taxes", icon: "percent", label: t("app.sidebar.payrollIncomeTaxes"), roles: [1, 2] },
        { path: "/payroll/salary-assignments", icon: "address card outline", label: t("app.sidebar.payrollAssignments"), roles: [1, 2] },
        { path: "/payroll/run-payroll", icon: "play circle", label: t("app.sidebar.payrollRun"), roles: [1, 2] },
        { path: "/payroll/bonuses", icon: "gift", label: t("app.sidebar.payrollBonuses"), roles: [1, 2] },
        { path: "/payroll/salary-increments", icon: "chart line", label: t("app.sidebar.payrollIncrements"), roles: [1, 2] },
        { path: "/payroll/salary-certificate-requests", icon: "file alternate outline", label: t("app.sidebar.payrollCertificates"), roles: [1, 2], visibleWhen: () => false },
        { path: "/payroll/payslip-preview", icon: "file pdf outline", label: t("app.sidebar.payrollPayslip"), roles: [1, 2] },
      ],
    },
    {
      key: "appraisal",
      icon: "clipboard check",
      label: t("app.sidebar.appraisal"),
      isSectionActive: (p) => p.startsWith("/appraisal"),
      onCollapsedClick: (rid, navigate) => {
        navigate(getAppraisalHomePath(rid));
      },
      children: [
        { path: "/appraisal/overview", icon: "chart bar", label: t("app.sidebar.appraisalOverview"), roles: [1, 2, 3] },
        // Admin setup / settings
        { path: "/appraisal/settings", icon: "setting", label: t("app.sidebar.appraisalSettings"), roles: [1, 2], dividerBefore: true },
        { path: "/appraisal/framework", icon: "sitemap", label: t("app.sidebar.appraisalFramework"), roles: [1, 2] },
        { path: "/appraisal/objectives", icon: "flag", label: t("app.sidebar.appraisalObjectives"), roles: [1, 2], featureFlag: "isKpi" },
        { path: "/appraisal/retrospectives", icon: "history", label: t("app.sidebar.appraisalRetrospectives"), roles: [1, 2], featureFlag: "isRetrospective" },
        { path: "/appraisal/lms", icon: "student", label: t("app.sidebar.appraisalLms"), roles: [1, 2], featureFlag: "isLms" },
        // Team
        { path: "/appraisal/team", icon: "users", label: t("app.sidebar.appraisalTeam"), roles: [1, 2, 3], dividerBefore: true },
        { path: "/appraisal/team-goals", icon: "check circle", label: t("app.sidebar.appraisalTeamGoals"), roles: [1, 2, 3], featureFlag: "isKpi" },
        { path: "/appraisal/team-pdp", icon: "student", label: t("app.sidebar.appraisalTeamPdp"), roles: [1, 2, 3], featureFlag: "isDevelopment" },
        { path: "/appraisal/team-one-to-one", icon: "talk", label: t("app.sidebar.appraisalTeamOneToOne"), roles: [1, 2, 3], featureFlag: "isOneToOne" },
        { path: "/appraisal/team-conversations", icon: "conversation", label: t("app.sidebar.appraisalTeamConversations"), roles: [1, 2, 3], featureFlag: "isOneToOneConversation" },
        // My (admins only when they have a line manager)
        { path: "/appraisal/self", icon: "user", label: t("app.sidebar.appraisalSelf"), roles: [1, 2, 3, 4], dividerBefore: true, visibleWhen: (rid) => canAccessMyAppraisal(rid) },
        { path: "/appraisal/goals", icon: "crosshairs", label: t("app.sidebar.appraisalGoals"), roles: [1, 2, 3, 4], featureFlag: "isKpi", visibleWhen: (rid) => canAccessMyAppraisal(rid) },
        { path: "/appraisal/evidence", icon: "folder open", label: t("app.sidebar.appraisalEvidence"), roles: [1, 2, 3, 4], visibleWhen: (rid) => canAccessMyAppraisal(rid) },
        { path: "/appraisal/pdp", icon: "book", label: t("app.sidebar.appraisalPdp"), roles: [1, 2, 3, 4], featureFlag: "isDevelopment", visibleWhen: (rid) => canAccessMyAppraisal(rid) },
        { path: "/appraisal/one-to-one", icon: "comment", label: t("app.sidebar.appraisalOneToOne"), roles: [1, 2, 3, 4], featureFlag: "isOneToOne", visibleWhen: (rid) => canAccessMyAppraisal(rid) },
        { path: "/appraisal/conversations", icon: "chat", label: t("app.sidebar.appraisalConversations"), roles: [1, 2, 3, 4], featureFlag: "isOneToOneConversation", visibleWhen: (rid) => canAccessMyAppraisal(rid) },
      ],
    },
    {
      path: "/settings/company",
      icon: "settings",
      label: t("app.sidebar.companySettings"),
      description: t("app.sidebar.companySettingsDescription"),
      roles: [1, 2],
    },
  ];

  const platformMenuItems = [
    {
      path: "/workplaces",
      icon: "shield",
      label: t("app.superAdmin.title", { defaultValue: "Super Admin" }),
      description: t("app.superAdmin.description", {
        defaultValue: "Platform overview — companies, users, payments, and tickets.",
      }),
    },
    {
      path: "/create-company",
      icon: "plus",
      label: t("app.companyDashboard.addCompany", { defaultValue: "Add Company" }),
      description: t("app.superAdmin.addCompanyDescription", {
        defaultValue: "Register a new company and admin",
      }),
    },
  ];

  const saPlatformMode = isSuperAdminPlatformMode();
  const saCompanyMode = isSuperAdminInCompany();
  const selectedCompany = saCompanyMode ? getSelectedCompany() : null;

  const filteredMenuItems = (saPlatformMode ? platformMenuItems : menuItems).filter((item) => {
    if (saPlatformMode) return true;
    if (
      SUBSCRIPTION_RESTRICTED_MENU_KEYS.has(item.key) &&
      (subscriptionLoading || isSuspended)
    ) {
      return false;
    }
    if (!item.roles) return true;
    return item.roles.includes(parseInt(userRoleId));
  });

  const isChildNavActive = (child) => {
    if (child.navId === "wallchart") {
      return location.pathname === "/wallchart" || location.pathname.includes("/time-off") || location.pathname.includes("/timeOff");
    }
    if (child.navId === "mycalendar") {
      return location.pathname.includes("/dashboard/calender");
    }
    if (child.path === "/leave/dashboard") {
      return location.pathname === "/leave/dashboard";
    }
    if (child.path === "/settings/leave") {
      if (!isLeaveSettingsPath()) return false;
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab") || "leavetypes";
      const expected = child.tab || "leavetypes";
      return tab === expected;
    }
    return child.path ? isActive(child.path) : false;
  };

  const handleChildNavClick = (child) => {
    if (child.requiresWorkplace) {
      withWorkplace(() => {
        const user = getUserFromStorage();
        if (child.navId === "wallchart") {
          navigateToWallChart(history, user);
        } else if (child.navId === "mycalendar") {
          navigateToMyCalendar(history, user);
        }
      });
      return;
    }
    if (child.path === "/settings/leave") {
      history.push(`/settings/leave?tab=${child.tab || "leavetypes"}`);
      return;
    }
    if (child.path) {
      handleRedirect(child.path);
    }
  };

  const isTopLevelNavActive = (item) => {
    if (item.path === "/settings/company" && item.tab === "notifications") {
      if (!isCompanySettingsPath()) return false;
      const params = new URLSearchParams(location.search);
      return params.get("tab") === "notifications";
    }
    return isActive(item.path);
  };

  const handleTopLevelNavClick = (item) => {
    if (item.path === "/settings/company" && item.tab) {
      history.push(`/settings/company?tab=${item.tab}`);
      return;
    }
    handleRedirect(item.path);
  };

  const renderNavItem = (item, index) => {
    const active = isTopLevelNavActive(item);
    const itemKey = item.tab ? `${item.path}-${item.tab}` : item.path || index;
    return (
      <button
        key={itemKey}
        type="button"
        onClick={() => handleTopLevelNavClick(item)}
        className={`sidebar-nav-item ${active ? "sidebar-nav-item-active" : ""}`}
        title={collapsed ? item.label : item.description || item.label}
        aria-current={active ? "page" : undefined}
      >
        <span className={`sidebar-nav-icon-wrap ${active ? "sidebar-nav-icon-wrap-active" : ""}`}>
          <Icon name={item.icon} className="sidebar-nav-icon" />
        </span>
        {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
        {active && !collapsed && <span className="sidebar-nav-active-indicator" aria-hidden />}
      </button>
    );
  };

  const renderNavGroup = (item, index) => {
    const childItems = item.children.filter((child) => {
      if (!child.roles) return true;
      const rid = parseInt(userRoleId, 10);
      if (!child.roles.includes(rid)) return false;
      if (typeof child.visibleWhen === "function" && !child.visibleWhen(rid)) return false;
      if (child.featureFlag) {
        // Hide optional modules until flags load, then only if enabled
        if (appraisalFeaturesLoading) return false;
        if (!isAppraisalFeatureEnabled(appraisalFeatures, child.featureFlag)) return false;
      }
      return true;
    });

    if (!childItems.length) return null;

    const sectionActive = item.isSectionActive ? item.isSectionActive(location.pathname) : false;
    const isExpanded = expandedGroups[item.key] === true;
    const hasActiveChild = childItems.some((child) => isChildNavActive(child));

    return (
      <div
        key={item.key || index}
        className={[
          "sidebar-nav-group",
          sectionActive ? "sidebar-nav-group--active" : "",
          isExpanded ? "sidebar-nav-group--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className={[
            "sidebar-nav-group-title",
            sectionActive && hasActiveChild ? "sidebar-nav-group-title--highlight" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-expanded={collapsed ? undefined : isExpanded}
          onClick={() => {
            if (collapsed) {
              const rid = parseInt(userRoleId, 10);
              if (item.onCollapsedClick) {
                item.onCollapsedClick(rid, handleRedirect);
              } else if (childItems[0]) {
                handleRedirect(childItems[0].path);
              }
            } else {
              toggleGroup(item.key);
            }
          }}
          title={collapsed ? item.label : undefined}
        >
          <span className="sidebar-nav-group-title-inner">
            <span
              className={[
                "sidebar-nav-icon-wrap",
                sectionActive && hasActiveChild ? "sidebar-nav-icon-wrap-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Icon name={item.icon} className="sidebar-nav-icon" />
            </span>
            {!collapsed && (
              <>
                <span className="sidebar-nav-label">{item.label}</span>
                <Icon
                  name="chevron down"
                  className={`sidebar-nav-group-chevron ${isExpanded ? "sidebar-nav-group-chevron--open" : ""}`}
                  aria-hidden
                />
              </>
            )}
          </span>
        </button>
        {!collapsed && isExpanded && (
          <div className="sidebar-nav-group-children">
            {childItems.map((child, childIndex) => {
              const active = isChildNavActive(child);
              const childKey = child.navId || child.path + (child.tab || "");
              const showDivider = child.dividerBefore === true && childIndex > 0;
              return (
                <React.Fragment key={childKey}>
                  {showDivider ? <div className="sidebar-nav-group-children-divider" aria-hidden /> : null}
                  <button
                    type="button"
                    onClick={() => handleChildNavClick(child)}
                    className={`sidebar-nav-sub-item ${active ? "sidebar-nav-sub-item-active" : ""}`}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="sidebar-nav-sub-rail" aria-hidden />
                    <Icon name={child.icon} className="sidebar-nav-sub-icon" />
                    <span className="sidebar-nav-sub-label">{child.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile && !collapsed && (
        <div
          className="sidebar-overlay"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}
      <Sidebar
        as={Menu}
        animation={isMobile ? "overlay" : "push"}
        icon="labeled"
        vertical
        visible={!isMobile || !collapsed}
        width="very thin"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          width: sidebarWidth,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.3s ease",
          zIndex: isMobile ? 1000 : 100,
        }}
        className={`sidebar sidebar-light ${collapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}
      >
        <div className="sidebar-header-light">
          <button
            type="button"
            onClick={onToggle}
            className="sidebar-toggle-btn-light"
            aria-label="Toggle menu"
          >
            <Icon name="bars" className="sidebar-toggle-icon" />
          </button>
          {!collapsed && (
            <div className="sidebar-brand-block">
              <span className="sidebar-brand-icon-wrap">
                <img
                  src="/assets/img/small-logo.png"
                  alt=""
                  className="sidebar-brand-logo"
                />
              </span>
              <span className="sidebar-brand-name">{t("app.sidebar.timeOffLedger")}</span>
            </div>
          )}
        </div>

        <nav className="sidebar-nav sidebar-nav-main" aria-label="Main navigation">
          {saCompanyMode && !collapsed && selectedCompany?.name ? (
            <div className="sidebar-sa-company-banner px-3 py-2 mb-2">
              <div className="small text-muted">Reviewing company</div>
              <div className="fw-semibold text-truncate" title={selectedCompany.name}>
                {selectedCompany.name}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary mt-2 w-100"
                onClick={() => exitSuperAdminCompany(history)}
              >
                Exit to Super Admin
              </button>
            </div>
          ) : null}
          {saCompanyMode && collapsed ? (
            <button
              type="button"
              className="sidebar-nav-item"
              title="Exit to Super Admin"
              onClick={() => exitSuperAdminCompany(history)}
            >
              <span className="sidebar-nav-icon-wrap">
                <Icon name="sign out" className="sidebar-nav-icon" />
              </span>
            </button>
          ) : null}
          {filteredMenuItems.map((item, index) =>
            item.children ? renderNavGroup(item, index) : renderNavItem(item, index)
          )}
        </nav>

        {!isSuperAdminRole(userRoleId) ? (
          <div className={`sidebar-footer ${collapsed ? "sidebar-footer-collapsed" : ""}`}>
            <button
              type="button"
              className="sidebar-help-support-btn"
              onClick={() => {
                history.push("/help-support");
              }}
              title={t("app.header.helpAndSupport")}
            >
              <span className="sidebar-help-support-icon-wrap" aria-hidden>
                <Icon name="question circle" />
              </span>
              {!collapsed && (
                <span className="sidebar-help-support-label">{t("app.header.helpAndSupport")}</span>
              )}
            </button>
          </div>
        ) : null}
      </Sidebar>

      {workplaceRequiredModal}
    </>
  );
};

export default SidebarMenu;
